import dayjs, { Dayjs } from 'dayjs';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Select,
  Upload,
  UploadFile,
  message
} from 'antd';
import {
  COMPLEMENTARY_ACTIVITY_ONLINE_LOCATION,
  isOnlineComplementaryActivityLocation
} from 'services/graduate-enrollment/complementary-activities';
import {
  ComplementaryActivityOrigin,
  IComplementaryActivity,
  IComplementaryActivityPayload
} from 'interfaces/complementary_activities';
import { UploadOutlined } from '@ant-design/icons';
import { toSaoPauloTimeZone } from 'services/date';
import { useEffect } from 'react';

const REQUIRED_MESSAGE = 'Campo obrigatório';

// Mesmo intervalo validado pelo Atlas em `Graduate::Courses::ComplementaryActivity`.
const COMPLEMENTARY_ACTIVITY_MIN_HOURS = 1;
const COMPLEMENTARY_ACTIVITY_MAX_HOURS = 500;

const CHOICE_CLASS =
  '!m-0 !flex !h-8 !w-full !items-center !rounded-md !border !border-solid !border-gray-300 !bg-white !px-3 hover:!border-gray-400 [&.ant-radio-wrapper-checked]:!border-primary-600 [&.ant-radio-wrapper-checked]:!bg-primary-100';

// Comprovante: PDF ou imagem, que é o que o visualizador do card consegue exibir.
const ACCEPTED_FILE_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const ACCEPTED_FILE_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png'];

const isAcceptedFile = ({ type, name }: { type?: string; name?: string }) =>
  ACCEPTED_FILE_TYPES.includes((type ?? '').toLowerCase()) ||
  ACCEPTED_FILE_EXTENSIONS.some(extension =>
    (name ?? '').toLowerCase().endsWith(extension)
  );

const ACTIVITY_TYPE_OPTIONS = [
  'Aproveitamento Acadêmico',
  'Curso Livre',
  'Outros'
] as const;

type ActivityTypeOption = (typeof ACTIVITY_TYPE_OPTIONS)[number];

const normalizeActivityType = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036F]/g, '')
    .trim()
    .toLowerCase();

const activityTypeFields = (activityType: string) => {
  const preset = ACTIVITY_TYPE_OPTIONS.find(
    option =>
      normalizeActivityType(option) === normalizeActivityType(activityType)
  );

  if (preset && preset !== 'Outros') {
    return { activity_type_option: preset };
  }

  if (preset === 'Outros') {
    return { activity_type_option: 'Outros' as const };
  }

  return {
    activity_type_option: 'Outros' as const,
    activity_type_detail: activityType
  };
};

const activityTypePayload = (option: ActivityTypeOption, detail?: string) => {
  if (option !== 'Outros') {
    return option;
  }

  return detail?.trim() || 'Outros';
};

// `online` é só da tela: a API recebe "Online" ou o local digitado em `location`.
const locationFields = (location: string) =>
  isOnlineComplementaryActivityLocation(location)
    ? { online: true, location: undefined }
    : { online: false, location };

interface FormValues {
  origin: ComplementaryActivityOrigin;
  activity_type_option: ActivityTypeOption;
  activity_type_detail?: string;
  event_name: string;
  online: boolean;
  location?: string;
  hours: number;
  starts_on: Dayjs;
  ends_on: Dayjs;
  file?: UploadFile[];
}

export interface ComplementaryActivityFormSubmit {
  payload: IComplementaryActivityPayload;
  file: File | null;
}

interface ComplementaryActivityFormModalProps {
  open: boolean;
  activity?: IComplementaryActivity;
  enrollmentCreatedAt?: string;
  loading: boolean;
  onCancel: () => void;
  onSubmit: (data: ComplementaryActivityFormSubmit) => void;
}

export const ComplementaryActivityFormModal = ({
  open,
  activity,
  enrollmentCreatedAt,
  loading,
  onCancel,
  onSubmit
}: ComplementaryActivityFormModalProps) => {
  const [form] = Form.useForm<FormValues>();
  const online = Form.useWatch('online', form);
  const activityTypeOption = Form.useWatch('activity_type_option', form);
  const startsOn = Form.useWatch('starts_on', form);
  const isEditing = Boolean(activity);

  useEffect(() => {
    if (!open) {
      return;
    }

    form.resetFields();

    if (activity) {
      form.setFieldsValue({
        origin: activity.origin,
        ...activityTypeFields(activity.activity_type),
        event_name: activity.event_name,
        ...locationFields(activity.location),
        hours: activity.hours,
        starts_on: dayjs(activity.starts_on),
        ends_on: dayjs(activity.ends_on)
      });
    }
  }, [open, activity, form]);

  useEffect(() => {
    if (online) {
      form.setFieldValue('location');
    }
  }, [online, form]);

  const handleFinish = (values: FormValues) => {
    onSubmit({
      file: (values.file?.[0]?.originFileObj as File | undefined) ?? null,
      payload: {
        origin: values.origin,
        activity_type: activityTypePayload(
          values.activity_type_option,
          values.activity_type_detail
        ),
        event_name: values.event_name,
        location: values.online
          ? COMPLEMENTARY_ACTIVITY_ONLINE_LOCATION
          : (values.location ?? '').trim(),
        hours: values.hours,
        starts_on: values.starts_on.format('YYYY-MM-DD'),
        ends_on: values.ends_on.format('YYYY-MM-DD')
      }
    });
  };

  // O período vai da data da matrícula até hoje: AAC é atividade já realizada.
  // O Atlas compara o início com o dia de `created_at` em São Paulo, então esse
  // dia é calculado nesse fuso, não no do navegador.
  const enrollmentDay =
    enrollmentCreatedAt &&
    toSaoPauloTimeZone(enrollmentCreatedAt).format('YYYY-MM-DD');

  const disabledStartDate = (current: Dayjs) =>
    current.isAfter(dayjs(), 'day') ||
    Boolean(enrollmentDay && current.format('YYYY-MM-DD') < enrollmentDay);

  // O período fim também não pode ser anterior ao início.
  const disabledEndDate = (current: Dayjs) =>
    disabledStartDate(current) ||
    Boolean(startsOn && current.isBefore(startsOn, 'day'));

  return (
    <Modal
      title={
        isEditing
          ? 'Editar atividade acadêmica complementar'
          : 'Cadastrar atividade acadêmica complementar'
      }
      open={open}
      onCancel={onCancel}
      onOk={form.submit}
      okText="Salvar"
      cancelText="Cancelar"
      confirmLoading={loading}
      destroyOnClose
      width={720}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ origin: 'internal', online: true }}
        onFinish={handleFinish}
      >
        <Form.Item
          label="Origem"
          name="origin"
          rules={[{ required: true, message: REQUIRED_MESSAGE }]}
        >
          <Radio.Group className="!grid w-full grid-cols-3 gap-x-4">
            <Radio className={CHOICE_CLASS} value="internal">
              Interna
            </Radio>
            <Radio className={CHOICE_CLASS} value="external">
              Externa
            </Radio>
          </Radio.Group>
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} md={16}>
            <Form.Item
              label="Tipo de atividade"
              name="activity_type_option"
              rules={[{ required: true, message: REQUIRED_MESSAGE }]}
            >
              <Select
                placeholder="Selecione"
                options={ACTIVITY_TYPE_OPTIONS.map(value => ({
                  value,
                  label: value
                }))}
              />
            </Form.Item>
          </Col>
          {activityTypeOption === 'Outros' && (
            <Col xs={24} md={8}>
              <Form.Item
                label="Especifique o tipo (opcional)"
                name="activity_type_detail"
              >
                <Input />
              </Form.Item>
            </Col>
          )}
        </Row>
        <Row gutter={16}>
          <Col xs={24} md={16}>
            <Form.Item
              label="Nome do evento"
              name="event_name"
              rules={[
                { required: true, whitespace: true, message: REQUIRED_MESSAGE }
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              label="Quantidade de horas"
              name="hours"
              rules={[
                { required: true, message: REQUIRED_MESSAGE },
                {
                  type: 'number',
                  min: COMPLEMENTARY_ACTIVITY_MIN_HOURS,
                  max: COMPLEMENTARY_ACTIVITY_MAX_HOURS,
                  message: `Informe de ${COMPLEMENTARY_ACTIVITY_MIN_HOURS} a ${COMPLEMENTARY_ACTIVITY_MAX_HOURS} horas`
                }
              ]}
            >
              <InputNumber
                min={COMPLEMENTARY_ACTIVITY_MIN_HOURS}
                max={COMPLEMENTARY_ACTIVITY_MAX_HOURS}
                precision={0}
                className="!w-full"
              />
            </Form.Item>
          </Col>
        </Row>

        <div className="grid w-full grid-cols-1 md:grid-cols-3 md:gap-x-4">
          <Form.Item
            className="md:col-span-2"
            label="Local"
            name="online"
            required
          >
            <Radio.Group className="!grid grid-cols-2 gap-x-4">
              <Radio className={CHOICE_CLASS} value={true}>
                Online
              </Radio>
              <Radio className={CHOICE_CLASS} value={false}>
                Presencial
              </Radio>
            </Radio.Group>
          </Form.Item>
          {online === false && (
            <Form.Item
              className="min-w-0"
              label="Onde a atividade ocorreu"
              name="location"
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: REQUIRED_MESSAGE
                }
              ]}
            >
              <Input />
            </Form.Item>
          )}
        </div>

        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item
              label="Período início"
              name="starts_on"
              rules={[{ required: true, message: REQUIRED_MESSAGE }]}
            >
              <DatePicker
                format="DD/MM/YYYY"
                className="w-full"
                disabledDate={disabledStartDate}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              label="Período fim"
              name="ends_on"
              dependencies={['starts_on']}
              rules={[
                { required: true, message: REQUIRED_MESSAGE },
                ({ getFieldValue }) => ({
                  validator: (_, value?: Dayjs) => {
                    const start: Dayjs | undefined = getFieldValue('starts_on');

                    return value && start && value.isBefore(start, 'day')
                      ? Promise.reject(
                          new Error('Não pode ser anterior ao início')
                        )
                      : Promise.resolve();
                  }
                })
              ]}
            >
              <DatePicker
                format="DD/MM/YYYY"
                className="w-full"
                disabledDate={disabledEndDate}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Comprovante"
          name="file"
          valuePropName="fileList"
          getValueFromEvent={({ fileList }) => fileList}
          rules={[{ required: !isEditing, message: REQUIRED_MESSAGE }]}
          extra={
            isEditing
              ? `PDF ou imagem (JPG, PNG). Atual: ${activity?.file_name}. Envie outro só para substituir.`
              : 'PDF ou imagem (JPG, PNG).'
          }
        >
          <Upload
            maxCount={1}
            accept={ACCEPTED_FILE_TYPES.join(',')}
            beforeUpload={selected => {
              if (!isAcceptedFile(selected)) {
                message.error(
                  `${selected.name} não é permitido. Envie um PDF ou imagem (JPG, PNG).`
                );
                return Upload.LIST_IGNORE;
              }

              return false;
            }}
          >
            <Button icon={<UploadOutlined />}>Selecionar arquivo</Button>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
};
